import React, { useState, useMemo, useEffect, useRef } from "react";
import { LeadViewEditViewFullInfoProps } from "./lead-view-edit-view-full-info.props";
import { Box } from "@mui/material";
import { Formik, Form } from "formik";
import {
  LEAD_STATUS_OPTIONS,
  LEAD_SOURCE_OPTIONS,
  INTERESTED_PRODUCT_OPTIONS,
  RATING_OPTIONS,
  PARTNER_LEAD_STATUS_OPTIONS,
} from "../../../../../../constants/leadDropdown";
import {
  TextInputComponent,
  SelectInputComponent,
  DescriptionInputComponent,
} from "../../../../../../common/components";
import { updateLeadDetails } from "../../../../../../api/lead";
import "./lead-view-edit-view-full-info.scss";
import { validationSchema } from "./lead-view-edit-view-full-info.validation";
import isEqual from "lodash/isEqual";
import CountryCodeWithPhoneNumber from "../../../../../../common/components/phone-number-input-component/country-code-phone-numner-input";
import { isOnlyCountryCode } from "../../../../../../utils/utils";
import { useLeadOwnerOptions } from "../../../../../../utils/getLeadOwnerOptions";
import ConfirmationPopup from "../../../../../../common/components/confirmation-popup/confirmation-popup";
import AlertIcon from "../../../../../../assets/icons/alert-icon.svg";
import {
  fetchPartnersList,
  fetchPartnersByRule,
} from "../../../../../../api/lead-assignment";
import { OptionType } from "../../../../lead-assignment/lead-assignment-props";
import SingleSelectAutocomplete from "../../../../lead-assignment/assignement-rules/single-option-select";
import { useLogin } from "../../../../../../common/hooks/useUser";
import AddressFormFields from "../../../../../../common/components/address-form-fields/address-form-fields";
import MODULES from "../../../../../../constants/modules";
import { useResourcePermissions } from "../../../../../../common/hooks/resource-permissions";
import DatePickerComponent from "../../../../../../common/components/date-picker/date-picker-compoenent";
import dayjs from "dayjs";
import { ALERT_TYPE } from "../../../../../../utils/constants";

const LeadViewEditViewFullInfo: React.FC<LeadViewEditViewFullInfoProps> = (
  props: any,
) => {
  const {
    leadDetails,
    setViewFullInfoOpen,
    selectedLeadId,
    handleCancelUpdate,
    setAlertType,
    setAlertMessage,
    setAlertOpen,
    setLoading,
    uploadedImageFile,
    removeImage,
    resetImageUpload,
    handleFormSubmitError,
    disableLeadStatus,
    isMobile,
  } = props;
  const formikRef: any = React.useRef(null);
  const LEAD_OWNER_OPTIONS = useLeadOwnerOptions(
    selectedLeadId,
    leadDetails?.owner,
  );
  const [isViewOpen, setIsViewOpen] = useState<boolean>(true);
  const [partnerList, setPartnerList] = useState([]);
  const [partnerListOptions, setPartnerListOptions] = useState<OptionType[]>([
    {
      label: leadDetails?.assignedTo?.company_name,
      value: leadDetails?.assignedTo?.company_name,
      id: leadDetails?.assigned_to,
    },
  ]);
  const { tenantDetails, userDetails } = useLogin();
  const [selectedPartnerId, setSelectedPartnerId] = useState(
    leadDetails?.assigned_to,
  );
  const disablePartnerAssignmentBasedOnLeadStatus = ["Closed-Won", "Converted"];
  const [isClickedAssignPartner, setIsClickedAssignPartner] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const assignPartnerDropdownRef = useRef<HTMLInputElement>(null);
  const [showConfirmationForClosedWon, setShowConfirmationForClosedWon] =
    useState(false);
  const [pendingSaveValues, setPendingSaveValues] = useState<any>(null);

  const checkIsAFPSuperAdmin = () => {
    if (
      tenantDetails?.tenantType === "SuperTenant" &&
      userDetails?.roles?.role === "Super Admin"
    )
      return true;
    return false;
  };

  const checkDisableField = () => {
    return tenantDetails?.tenantType === "SuperTenant" &&
      leadDetails?.assigned_to &&
      !(leadDetails?.lead_status === "Closed-Lost")
      ? true
      : false;
  };

  const { canWrite } = useResourcePermissions(MODULES.ASSIGN_LEAD_OWNER);

  const checkDisableAssignPartnerDropdown = () => {
    return (
      disablePartnerAssignmentBasedOnLeadStatus.includes(
        leadDetails?.lead_status,
      ) ||
      !checkIsAFPSuperAdmin() ||
      !isClickedAssignPartner
    );
  };

  const address = leadDetails?.address || {};
  const hasAnyAddressField = !!(
    address.city ||
    address.state ||
    address.country ||
    address.zipcode
  );

  const interestedProductsArray = (leadDetails?.interested_products || "")
    .split(",")
    .map((item: string) => item.trim());

  const hasValidProduct =
    interestedProductsArray.includes("At-Need") ||
    interestedProductsArray.includes("Pre-Need");

  const isAssignPartnerBtnEnabled = hasAnyAddressField && hasValidProduct;

  const checkDisableLeadStatus = () => {
    return checkDisableField() && !(leadDetails?.lead_status === "Closed-Lost")
      ? true
      : false;
  };

  const sanitize = (value: any): string => {
    return value === null || value === undefined || value === "null"
      ? ""
      : value;
  };
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingLeadStatus, setPendingLeadStatus] = useState<string | null>(
    null,
  );
  const initialValues = useMemo(
    () => ({
      title: sanitize(leadDetails?.title),
      company: sanitize(leadDetails?.company),
      assignedPartner: sanitize(leadDetails?.assignedTo?.company_name),
      firstName: sanitize(leadDetails?.first_name),
      lastName: sanitize(leadDetails?.last_name),
      email: sanitize(leadDetails?.primary_email_address),
      secondaryEmail: sanitize(leadDetails?.secondary_email_address),
      fax: sanitize(leadDetails?.fax_number),
      website: sanitize(leadDetails?.website_url),
      numEmployees: sanitize(leadDetails?.number_of_employees),
      annualRevenue: sanitize(leadDetails?.annual_revenue),
      socialLeadId: sanitize(leadDetails?.social_lead_id),
      streetLine1: sanitize(leadDetails?.address?.street_line_1),
      streetLine2: sanitize(leadDetails?.address?.street_line_2),
      description: sanitize(leadDetails?.description),
      city: sanitize(leadDetails?.address?.city),
      state: sanitize(leadDetails?.address?.state),
      country: sanitize(leadDetails?.address?.country),
      zipcode: sanitize(leadDetails?.address?.zipcode),
      addressId: sanitize(leadDetails?.address?.id),
      interestedProduct: Array.isArray(leadDetails?.interested_products)
        ? leadDetails.interested_products
        : typeof leadDetails?.interested_products === "string" &&
            leadDetails?.interested_products !== "null"
          ? leadDetails.interested_products.split(",").map((s: any) => s.trim())
          : [],
      selectedLeadOwner: `${leadDetails?.owner?.first_name} ${leadDetails?.owner?.last_name}`,
      selectedLeadSource: leadDetails?.lead_source ?? null,
      selectedLeadStatus: leadDetails?.lead_status ?? null,
      rating: sanitize(leadDetails?.confidence_score) ?? null,
      industry: sanitize(leadDetails?.industry) ?? null,
      primaryPhoneNumber: sanitize(leadDetails.primary_phone_number) || null,
      secondaryPhoneNumber:
        sanitize(leadDetails.secondary_phone_number) || null,
      designation: leadDetails?.designation || null,
      middleName: leadDetails?.middle_name || null,
      age: leadDetails?.age || null,
      leadValue: leadDetails?.lead_value || null,
      maritalStatus: leadDetails?.marital_status || null,
      relationship: leadDetails?.relationship || null,
      deathDate: leadDetails?.death_date
        ? dayjs(leadDetails?.death_date)
        : null,
      contactPerson: leadDetails?.contact_person || null,
      contactPersonPhoneNumber: leadDetails?.contact_person_phone_number || null,
    }),
    [leadDetails],
  );

  const handleCancel = () => {
    resetImageUpload?.();
    setViewFullInfoOpen(false);
  };

  const parseLeadNotes = (notes: string) => {
    if (!notes) return [];

    return notes.split("\n").map((line) => {
      const colonIndex = line.indexOf(":");

      if (colonIndex === -1) {
        return {
          title: "",
          value: line.trim(),
        };
      }

      return {
        title: line.slice(0, colonIndex).trim(),
        value: line.slice(colonIndex + 1).trim(),
      };
    });
  };

  const handleSave = async (values: any) => {
    setLoading(true);
    try {
      const formData = new FormData();
      const payload = {
        firstName: values.firstName,
        lastName: values.lastName || null,
        primaryEmail: values.email,
        assignedTo: parseInt(selectedPartnerId, 10) || null,
        secondaryEmail: values.secondaryEmail || null,
        ...(isOnlyCountryCode(values.primaryPhoneNumber)
          ? {}
          : { primaryPhoneNumber: values.primaryPhoneNumber || null }),
        ...(isOnlyCountryCode(values.secondaryPhoneNumber)
          ? {}
          : { secondaryPhoneNumber: values.secondaryPhoneNumber || null }),
        website: values.website || null,
        address: {
          streetLine1: values.streetLine1 || null,
          streetLine2: values.streetLine2 || null,
          city: values.city || null,
          state: values.state || null,
          country: values.country || null,
          zipcode: values.zipcode || null,
        },
        leadSource: values.selectedLeadSource || null,
        leadStatus: values.selectedLeadStatus ?? null,
        faxNumber: isOnlyCountryCode(values.fax) ? null : values.fax || null,
        industry: values.industry || null,
        interestedProducts:
          Array.isArray(values?.interestedProduct) &&
          values?.interestedProduct.length > 0
            ? values?.interestedProduct?.join(", ")
            : null,
        socialLeadId: values.socialLeadId || null,
        description: values.description || null,
        confidenceScore: values.rating || null,
        designation: values.designation || null,
        middleName: values.middleName || null,
        age: parseInt(values.age || 0) || null,
        leadValue: values.leadValue || null,
        maritalStatus: values.maritalStatus || null,
        page: "first",
        company: values?.company || null,
        preferredContactMode: leadDetails?.preferred_contact_mode || null,
        deathDate: values.deathDate
          ? dayjs(values.deathDate).format("YYYY-MM-DD")
          : null,
        contactPerson: values.contactPerson || null,
        relationship: values.relationship || null,
        contactPersonPhoneNumber: isOnlyCountryCode(values.contactPersonPhoneNumber) ? null : values.contactPersonPhoneNumber || null,
      };

      Object.entries(payload).forEach(([key, value]) => {
        if (key !== "imageUrl" && key !== "address" && value !== null) {
          formData.append(key, value);
        }
      });
      console.log(values.imageUrl instanceof File);
      if (uploadedImageFile) {
        formData.append("leadImg", uploadedImageFile);
      } else if (removeImage && !uploadedImageFile) {
        formData.append("removeLeadImage", "true");
      }
      [
        "streetLine1",
        "streetLine2",
        "city",
        "state",
        "country",
        "zipcode",
      ].forEach((field) => {
        if (values[field]) {
          formData.append(
            `address[${field.replace("Address", "")}]`,
            values[field],
          );
        }
      });
      if (values.addressId) {
        formData.append("address[id]", values.addressId);
      }
      if (values.imageUrl instanceof File) {
        formData.append("leadImg", uploadedImageFile);
      }
      const response = await updateLeadDetails(selectedLeadId, formData);
      if (response.data.success) {
        if (props.onLeadNameUpdate) {
          props.onLeadNameUpdate(
            `${values.firstName} ${values.middleName || ""} ${values.lastName}`.trim(),
          );
        }
        setAlertMessage("Lead updated successfully");
        setAlertType(ALERT_TYPE.SUCCESS);
        setAlertOpen(true);
        handleCancelUpdate();
        setLoading(false);
      } else {
        setAlertMessage("Failed to save lead.");
        setAlertType(ALERT_TYPE.ERROR);
        setAlertOpen(true);
        setLoading(false);
      }
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message ||
          "An error occurred while saving the lead.",
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };

  const fetchPartners = async () => {
    const rawAddress = {
      city: leadDetails?.address?.city,
      state: leadDetails?.address?.state,
      country: leadDetails?.address?.country,
      postcode: leadDetails?.address?.zipcode,
    };

    // Remove keys with null or undefined values
    const address = Object.fromEntries(
      Object.entries(rawAddress).filter(([_, v]) => v != null),
    );
    const productType = interestedProductsArray.filter((item: string) =>
      ["At-Need", "Pre-Need"].includes(item),
    );
    const params = {
      isActive: true,
      address: JSON.stringify(address),
      dropDown: true,
      productType: productType.length > 0 ? productType : [],
    };
    setLoading(true);
    try {
      const response = await fetchPartnersList(params);
      if (response.data.success) {
        setPartnerList(response.data.data);
      }
    } catch (error: any) {
      console.log("error message,,,", error);
      setAlertMessage(
        error?.response?.data?.message ||
          "An error occurred while getting the partners list.",
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (checkIsAFPSuperAdmin() && isClickedAssignPartner) fetchPartners();
  }, [isClickedAssignPartner, leadDetails?.id]);

  useEffect(() => {
    if (Array.isArray(partnerList)) {
      const options = partnerList.map((partner: any) => ({
        label: partner.company_name,
        value: partner.company_name,
        id: partner.id,
      }));
      setPartnerListOptions(options);
    }
    checkIsAFPSuperAdmin();
  }, [partnerList]);

  const checkAddDetails = (errors: any) => {
    Object.keys(errors)?.forEach((key) => {
      const isFromDetailsTab = ["secondaryPhoneNumber", "leadValue"]?.includes(
        key,
      );
      if (isFromDetailsTab) {
        handleFormSubmitError("additionalDetails");
      }
    });
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema(true)}
      onSubmit={(values) => handleSave(values)}
      enableReinitialize={true}
      innerRef={formikRef}
    >
      {({
        values,
        setFieldValue,
        handleSubmit,
        setFieldTouched,
        touched,
        errors,
      }) => {
        const isFormDirty = !isEqual(values, initialValues);

        return (
          <Form>
            <div className="lead-list-view-details-view-box-full-info">
              {!isMobile && (
                <div className="lead-list-view-details-view-box-full-info-header">
                  <div>Details</div>
                  <div>
                    {isViewOpen ? (
                      <i
                        className="fa fa-angle-down"
                        onClick={() => {
                          setIsViewOpen(!isViewOpen);
                        }}
                      />
                    ) : (
                      <i
                        className="fa fa-angle-up"
                        onClick={() => {
                          setIsViewOpen(!isViewOpen);
                        }}
                      />
                    )}
                  </div>
                </div>
              )}

              {isViewOpen ? (
                <>
                  <div className="lead-list-view-details-view-box-full-info-header-text">
                    Lead Information
                  </div>
                  <div className="lead-list-view-details-view-box-full-info-data">
                    <div className="lead-list-view-details-view-box-full-info-data-section">
                      <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="lead-list-view-details-view-box-full-info-data-section-main">
                          First Name
                        </div>
                        <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                          <TextInputComponent
                            type="text"
                            fieldName="firstName"
                            placeholder="Enter First Name"
                            optional={true}
                            disabled={checkDisableField()}
                            value={values.firstName}
                            onChange={(e: any) =>
                              setFieldValue("firstName", e?.target?.value)
                            }
                            isName={true}
                            minlength="3"
                            maxlength="50"
                          />
                        </div>
                      </div>
                      <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="lead-list-view-details-view-box-full-info-data-section-main">
                          Last Name
                        </div>
                        <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                          <TextInputComponent
                            type="text"
                            fieldName="lastName"
                            placeholder="Enter Last Name"
                            optional={true}
                            disabled={checkDisableField()}
                            value={values.lastName}
                            onChange={(e: any) =>
                              setFieldValue("lastName", e?.target?.value)
                            }
                            isName={true}
                            minlength="3"
                            maxlength="50"
                          />
                        </div>
                      </div>
                      <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="lead-list-view-details-view-box-full-info-data-section-main">
                          Lead Source
                        </div>
                        <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                          <SelectInputComponent
                            fieldName="leadSource"
                            options={LEAD_SOURCE_OPTIONS}
                            onChange={(e: any) => {
                              setFieldValue("selectedLeadSource", e);
                            }}
                            value={values?.selectedLeadSource}
                            notEditable={true}
                            disabled={checkDisableField()}
                          />
                        </div>
                      </div>
                      <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="lead-list-view-details-view-box-full-info-data-section-main">
                          Primay Phone Number
                        </div>
                        <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                          <CountryCodeWithPhoneNumber
                            fieldName="primaryPhoneNumber"
                            placeholder={
                              values?.primaryPhoneNumber
                                ? ""
                                : "Enter Primary Phone Number"
                            }
                            onChange={(
                              value: string,
                              country: any,
                              one: any,
                              two: any,
                            ) => {
                              const countryCode = country?.dialCode;

                              if (!countryCode) {
                                console.warn(
                                  "Missing dialCode in country object",
                                );
                                return;
                              }

                              if (`+${countryCode}` == two) {
                                setFieldValue("primaryPhoneNumber", null);
                              } else {
                                setFieldValue("primaryPhoneNumber", two);
                              }
                            }}
                            onBlur={() => {
                              setFieldTouched("primaryPhoneNumber", true);
                            }}
                            value={values?.primaryPhoneNumber || "+1"}
                            defaultCountry="us"
                            optional={true}
                            // countryCodeEditable
                            disabled={checkDisableField()}
                          />
                          {touched.primaryPhoneNumber &&
                            touched?.primaryPhoneNumber && (
                              <div className="error-message">
                                {errors.primaryPhoneNumber}
                              </div>
                            )}
                        </div>
                      </div>
                      <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="lead-list-view-details-view-box-full-info-data-section-main">
                          Confidence score
                        </div>
                        <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                          <SelectInputComponent
                            fieldName="rating"
                            options={RATING_OPTIONS}
                            onChange={(e: any) => {
                              setFieldValue("rating", e);
                            }}
                            value={values.rating}
                            placeholder="Select Confidence Score"
                            colorProp={true}
                            closeIcon
                            onClear={() => setFieldValue("rating", "")}
                            disabled={checkDisableField()}
                            notEditable={checkDisableField()}
                          />
                        </div>
                      </div>
                      {tenantDetails?.tenantType !== "Tenant" && (
                        <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                          <div className="lead-list-view-details-view-box-full-info-data-section-main">
                            Assigned Partner
                          </div>
                          <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                            <div className="assign-partner-dropdown form-select-input-component">
                              <SingleSelectAutocomplete
                                options={partnerListOptions}
                                selectedValue={
                                  partnerListOptions.find(
                                    (opt) =>
                                      opt.value === values?.assignedPartner,
                                  ) || null
                                }
                                placeholder="Select Partner"
                                noOptionsFoundText="No partners found"
                                fieldName={"assign-partner"}
                                onChange={(val: any) => {
                                  setFieldValue(
                                    "assignedPartner",
                                    val?.value || "",
                                  );
                                  setSelectedPartnerId(val?.id);
                                  if (leadDetails?.assigned_to && !val)
                                    setErrorMessage(
                                      "You can't un-assign lead to partner",
                                    );
                                  else if (
                                    leadDetails?.assigned_to &&
                                    values?.selectedLeadStatus === "Closed-Lost"
                                  ) {
                                    setErrorMessage(
                                      "Lead status must be Qualified to assign a partner",
                                    );
                                  } else setErrorMessage("");
                                }}
                                disable={
                                  checkDisableAssignPartnerDropdown() ||
                                  !(
                                    (values?.selectedLeadStatus ===
                                      "Qualified" &&
                                      !leadDetails?.assigned_to) ||
                                    (values?.selectedLeadStatus ===
                                      "Qualified" &&
                                      leadDetails?.lead_status ===
                                        "Closed-Lost" &&
                                      leadDetails?.assigned_to) ||
                                    (values?.selectedLeadStatus ===
                                      "Closed-Lost" &&
                                      leadDetails?.assigned_to)
                                  )
                                }
                                inputRef={assignPartnerDropdownRef}
                              />
                            </div>
                            <div className="assign-partner-btn-div">
                              <button
                                className="assign-partner-btn"
                                type="button"
                                onClick={() => {
                                  setIsClickedAssignPartner(true);
                                  setTimeout(() => {
                                    assignPartnerDropdownRef?.current?.focus();
                                    const keyboardEvent = new KeyboardEvent(
                                      "keydown",
                                      {
                                        bubbles: true,
                                        cancelable: true,
                                        key: "ArrowDown",
                                        code: "ArrowDown",
                                        keyCode: 40,
                                      },
                                    );
                                    // to manually open the dropdown
                                    assignPartnerDropdownRef?.current?.dispatchEvent(
                                      keyboardEvent,
                                    );
                                  }, 10);
                                }}
                                disabled={
                                  !checkIsAFPSuperAdmin() ||
                                  !isAssignPartnerBtnEnabled ||
                                  !(
                                    (values?.selectedLeadStatus ===
                                      "Qualified" &&
                                      !leadDetails?.assigned_to) ||
                                    (values?.selectedLeadStatus ===
                                      "Qualified" &&
                                      leadDetails?.lead_status ===
                                        "Closed-Lost" &&
                                      leadDetails?.assigned_to) ||
                                    (values?.selectedLeadStatus ===
                                      "Closed-Lost" &&
                                      !!leadDetails?.assigned_to)
                                  )
                                }
                              >{`${leadDetails?.assigned_to ? "Re Assign Partner" : "Assign Partner"}`}</button>
                            </div>
                            {errorMessage && (
                              <div className="error-message">
                                {errorMessage}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="lead-list-view-details-view-box-full-info-data-section">
                      <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="lead-list-view-details-view-box-full-info-data-section-main">
                          Middle Name
                        </div>
                        <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                          <TextInputComponent
                            type="text"
                            fieldName="middleName"
                            placeholder="Enter Middle Name"
                            optional={false}
                            disabled={checkDisableField()}
                            value={values.middleName}
                            onChange={(e: any) =>
                              setFieldValue("middleName", e?.target?.value)
                            }
                            isName={true}
                            minlength="3"
                            maxlength="50"
                          />
                        </div>
                      </div>
                      <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="lead-list-view-details-view-box-full-info-data-section-main">
                          Lead Owner
                        </div>
                        <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                          <SelectInputComponent
                            fieldName="leadOwner"
                            options={LEAD_OWNER_OPTIONS}
                            onChange={(e: any) => {
                              setFieldValue("selectedLeadOwner", e);
                            }}
                            value={values?.selectedLeadOwner}
                            placeholder="Enter lead owner"
                            disabled={checkDisableField()}
                            notEditable={checkDisableField() || !canWrite}
                          />
                        </div>
                      </div>
                      <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="lead-list-view-details-view-box-full-info-data-section-main">
                          Email
                        </div>
                        <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                          <TextInputComponent
                            type="email"
                            fieldName="email"
                            placeholder="Enter Email"
                            optional={false}
                            disabled={checkDisableField()}
                            value={values.email}
                            onChange={(e: any) =>
                              setFieldValue("email", e?.target?.value)
                            }
                          />
                        </div>
                      </div>
                      <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="lead-list-view-details-view-box-full-info-data-section-main">
                          Interested Product
                        </div>
                        <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                          <SelectInputComponent
                            fieldName="interestedProduct"
                            options={INTERESTED_PRODUCT_OPTIONS}
                            onChange={(e: any) => {
                              setFieldValue("interestedProduct", e);
                            }}
                            value={values?.interestedProduct}
                            multiple={true}
                            placeholder="Select Interested Product"
                            disabled={checkDisableField()}
                            notEditable={checkDisableField()}
                          />
                        </div>
                      </div>
                      <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="lead-list-view-details-view-box-full-info-data-section-main">
                          Lead Status
                        </div>
                        <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                          <SelectInputComponent
                            fieldName="leadStatus"
                            options={
                              leadDetails?.lead_source === "AFP"
                                ? PARTNER_LEAD_STATUS_OPTIONS
                                : LEAD_STATUS_OPTIONS
                            }
                            onChange={(e: any) => {
                              if (e === "Converted") {
                                setPendingLeadStatus(e);
                                setShowConfirmation(true);
                              } else {
                                setFieldValue("selectedLeadStatus", e);
                              }
                              if (e !== "Qualified" && selectedPartnerId) {
                                console.log("lead status in on change", e);
                                setErrorMessage(
                                  `Lead status must be Qualified to assign a partner`,
                                );
                              } else if (
                                e === "Qualified" &&
                                selectedPartnerId
                              ) {
                                setErrorMessage("");
                              }
                            }}
                            value={values?.selectedLeadStatus}
                            disabled={
                              checkDisableLeadStatus() || disableLeadStatus
                            }
                            notEditable={
                              checkDisableLeadStatus() || disableLeadStatus
                            }
                          />
                        </div>
                      </div>
                      <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="lead-list-view-details-view-box-full-info-data-section-main">
                          Death Date
                        </div>
                        <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                          <DatePickerComponent
                            label="Death Date"
                            fieldName="deathDate"
                            value={values?.deathDate || null}
                            onChange={(date) =>
                              setFieldValue("deathDate", date)
                            }
                            maxDate={dayjs().startOf("day")}
                            optional={true}
                            isLabel={false}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="lead-list-view-details-view-box-full-info-header-text">
                    Address
                  </div>

                  <Box
                    className="tenant-basic-details-content tenant-edit-basic-details-wrapper"
                    gap={3}
                  >
                    <AddressFormFields
                      postCode="zipcode"
                      isOutlineInputs
                      disabled={checkDisableField()}
                    />
                  </Box>

                  <div className="lead-list-view-details-view-box-full-info-header-text">
                    Description
                  </div>
                  <div className="lead-list-view-details-view-box-full-info-description-box">
                    <DescriptionInputComponent
                      fieldName="description"
                      placeholder="Add Description"
                      optional={false}
                      disabled={checkDisableField()}
                      value={values.description}
                      onChange={(e: any) =>
                        setFieldValue("description", e?.target?.value)
                      }
                      maxlength={150}
                    />
                  </div>
                  {leadDetails?.leadNotes?.length > 0 && (
                    <>
                      <div className="lead-list-view-details-view-box-full-info-header-text">
                        Notes
                      </div>
                      <div className="lead-list-view-details-view-box-full-info-description-box description-box-container">
                        {leadDetails?.leadNotes?.length
                          ? leadDetails.leadNotes.map((noteObj: any) => {
                              const parsedNotes = parseLeadNotes(noteObj.notes);

                              return (
                                <div
                                  key={noteObj.id}
                                  className="lead-notes-wrapper"
                                >
                                  {parsedNotes.map((item, index) => (
                                    <div key={index} className="lead-note-item">
                                      {item.title && (
                                        <div className="lead-note-title">
                                          {item.title}
                                        </div>
                                      )}
                                      <div className="lead-note-value">
                                        {item.value}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              );
                            })
                          : "-"}
                      </div>
                    </>
                  )}
                </>
              ) : (
                ""
              )}
            </div>
            <Box className="footer">
              <button
                type="button"
                className="cancel-button"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  checkAddDetails(errors);
                  if (values?.selectedLeadStatus === "Closed-Won") {
                    setPendingSaveValues(values);
                    setShowConfirmationForClosedWon(true);
                  } else {
                    handleSubmit();
                  }
                }}
                className="cancel-button next-button-wrapper"
                disabled={
                  !(isFormDirty || uploadedImageFile || removeImage) ||
                  !!errorMessage
                }
              >
                Save
              </button>
            </Box>
            <ConfirmationPopup
              title="Update Lead Status"
              open={showConfirmation}
              note="Do you want to close this lead in the CRM"
              onCancel={() => {
                setFieldValue("selectedLeadStatus", "Converted");
                setShowConfirmation(false);
                setPendingLeadStatus(null);
              }}
              onOkay={() => {
                setFieldValue("selectedLeadStatus", "Closed-Won");
                setShowConfirmation(false);
                setPendingLeadStatus(null);
              }}
              buttonText="Confirm"
              Icon={AlertIcon}
            />
            <ConfirmationPopup
              title="Convert Lead to Customer"
              open={showConfirmationForClosedWon}
              note="Are you sure you want to convert this lead into a customer? All lead information will be transferred to the customer record"
              onCancel={() => {
                setShowConfirmationForClosedWon(false);
              }}
              onOkay={() => {
                setShowConfirmationForClosedWon(false);
                handleSave(pendingSaveValues);
              }}
              buttonText="Confirm"
              Icon={AlertIcon}
            />
          </Form>
        );
      }}
    </Formik>
  );
};

export default LeadViewEditViewFullInfo;
